<template>

	<div class="mt-12">
		<div class="grid w-full" :class="[showPupilColumn? 'grid-cols-[1fr_1fr_1.5fr_2fr_.5fr]' : 'grid-cols-[2fr_3fr_3fr_1fr]']">

			<!-- Header row -->
			<div class="font-semibold text-sm py-3 px-4">
				<SortableHeader property="date" :order="sortOrder" @change="$emit('sortOrderChanged', $event)">{{ $t('Registered at') }}</SortableHeader>
			</div>
			<div v-if="showPupilColumn" class="font-semibold text-sm py-3 px-4">
				<SortableHeader property="pupil.firstName" :order="sortOrder" @change="$emit('sortOrderChanged', $event)">{{ $tc('Pupil', 1) }}</SortableHeader>
			</div>
			<div class="font-semibold text-sm py-3 px-4">
				<SortableHeader property="activity.name" :order="sortOrder" @change="$emit('sortOrderChanged', $event)">{{ $tc('Activity', 1) }}</SortableHeader>
			</div>
			<div class="font-semibold text-sm py-3 px-4">{{ $tc('Observation', 1) }}</div>
			<div class="font-semibold text-sm py-3 px-4"><span class="sr-only">{{ $t('Actions') }}</span></div>


			<!-- Observation rows -->
			<template v-for="(observation, key) in observations" :key="key">
				<div class="py-3 px-4 border-t border-base-200">
					<div>{{ registeredObservationDateFormat(observation.date) }}</div>
					<div class="text-base-content-light text-sm">{{ localTime(observation.date) }}</div>
				</div>

				<div v-if="showPupilColumn" class="flex items-start gap-2 py-3 px-4 border-t border-base-200">
					<PupilSymbol :pupil="observation.pupil" class="w-6 shrink-0" />
					<span>{{ observation.pupil?.firstName }} {{ observation.pupil?.lastName }}</span>
				</div>

				<div class="py-3 px-4 border-t border-base-200">
					<Tag v-if="showClassroomTag(observation)" class="tag-xs tag-sky mb-2" :title="$tc('Classroom', 1)">
						{{ observation.classroomName }}
					</Tag>
					<div v-if="observation.pupilActivity?.lessonItem?.getDescriptiveTitle()">
						<div class="font-semibold">{{ observation.pupilActivity?.lessonItem?.getDescriptiveTitle() }}</div>
						<div v-if="observation.pupilActivity?.lessonItem?.lesson" class="flex items-center gap-2 text-xs text-base-content-light whitespace-nowrap">
							<CalendarIcon class="w-3 h-3 text-base-content-light" />
							{{ activityTimeRange(observation.pupilActivity.lessonItem.lesson) }}
						</div>
					</div>
					<div v-else-if="observation.lessonItem?.lessonPlan">
						<div class="font-semibold">{{ observation.lessonItem?.lessonPlan.title }}</div>
						<div v-if="observation.lessonItem?.lesson" class="flex items-center gap-2 text-xs text-base-content-light whitespace-nowrap">
							<CalendarIcon class="w-3 h-3 text-base-content-light" />
							{{ activityTimeRange(observation.lessonItem.lesson) }}
						</div>
					</div>
					<div v-else-if="observation.lesson">
						<div class="font-semibold">{{ observation.lesson?.title }}</div>
						<div class="flex items-center gap-2 text-xs text-base-content-light whitespace-nowrap">
							<CalendarIcon class="w-3 h-3 text-base-content-light" />
							{{ activityTimeRange(observation.lesson) }}
						</div>
					</div>
					<button v-if="getGoals(observation).length > 0" class="btn btn-secondary btn-outline btn-sm mt-3.5 flex items-center gap-2 text-xs" @click="handleEvaluateObservation(observation)">
						<span class="flex items-center gap-2">
							<MapPinIcon class="w-3 h-3 shrink-0" />
							<span v-if="isReadOnly(observation)">
								{{ getGoals(observation).length }} {{ $tc('Goal', getGoals(observation).length).toLowerCase() }}
							</span>
							<span v-else class="line-clamp-2">
								{{ $tc('Evaluate') }} {{ getGoals(observation).length }} {{ $tc('Goal', getGoals(observation).length).toLowerCase() }}
							</span>
						</span>
					</button>
				</div>

				<div class="break-normal whitespace-pre-line text-sm py-3 px-4 border-t border-base-200 flex flex-col gap-4">
					<div v-if="!observation.author.isMe()" class="flex items-center gap-2">
						<UserAvatar :user="observation.author" class="w-6 h-6"/>
						<div class="text-base-content-light text-sm">
							{{ observation.author.getNameIdentifier() }}:
						</div>
					</div>
					<div v-if="observation.note">
						“{{ observation.note }}”
					</div>
					<div v-if="imageFilesFor(observation).length > 0" :data-observation-id="observation.id" class="flex w-fit items-center gap-4 p-2.5 rounded-lg bg-base-100 overflow-hidden">
						<template v-for="(file, index) in imageFilesFor(observation).slice(0, maxThumbnails)" :key="file.id ?? index">
							<button type="button" class="w-24 h-24 shrink-0 cursor-zoom-in" @click="openFancyboxAt(observation, index)">
								<img :src="file.getVariationOrDefault('sm').url" class="w-full h-full object-cover rounded-md" alt="" />
							</button>
						</template>
						<button v-if="hiddenImageCount(observation) > 0" class="w-24 h-24 shrink-0 rounded-lg bg-base-200 flex items-center justify-center text-sm font-semibold text-base-content hover:bg-base-300 transition" @click="openFancyboxAt(observation, maxThumbnails)">
							+{{ $tc('{x} more', { 'x': hiddenImageCount(observation) }).toLowerCase() }}
						</button>
					</div>
				</div>

				<div class="flex justify-end py-3 px-4 border-t border-base-200">
					<div v-if="!readOnly && observation.author.isMe()" class="print:hidden">
						<button class="btn btn-ghost btn-circle bg-base-100" @click="handleActivityEditRequest(observation)">
							<PencilIcon class="w-5 h-5" />
						</button>
					</div>
				</div>
			</template>

		</div>
	</div>

	<ObservationEditor v-if="showObservationEditor" @requestClose="closeObservationForm" :observation="editableModel" />

	<PupilGoalEvaluation
		v-if="evaluatingObservation"
		:pupil="evaluatingObservation.pupil"
		:goals="getGoals(evaluatingObservation)"
		:readOnly="isReadOnly(evaluatingObservation)"
		@requestClose="closeEvaluation"
	>
		<template #topMessage v-if="observationSaved">
			<div class="flex items-start p-2 mb-2 bg-green-100 text-success-content rounded">
				<p>{{ evaluationSuccessMessage }}</p>
			</div>
			<div class="flex items-start gap-3 p-2 mb-8 font-bold">
				<p>{{ $t('Evaluate the activity/lesson goals right now?') }}</p>
			</div>
		</template>
	</PupilGoalEvaluation>

</template>

<script lang="ts">
import { DateTime } from "luxon";
import { Fancybox as FancyboxLib } from "@fancyapps/ui";

import { mapStores } from "pinia";
import type { PropType } from "vue";
import { Observation } from "@/models/Observation.model";
import { OrderParameter } from "@/models/OrderParameter.model";
import type { Goal } from "@/models/Goal.model";
import { useOrganisationsStore } from "@/stores/Organisations.store";

import ObservationEditor from "@/components/ObservationEditor.vue";
import ObservationsFilter from "@/components/ObservationsFilter.vue";
import PupilGoalEvaluation from "@/components/PupilGoalEvaluation.vue";
import SortableHeader from "@/components/ui/SortableHeader.vue";
import Tag from "@/components/ui/Tag.vue";
import PupilSymbol from "@/components/ui/symbols/PupilSymbol.vue";
import UserAvatar from '@/components/UserAvatar.vue';

import { PlusIcon, PencilIcon } from '@heroicons/vue/24/outline';
import { CalendarIcon, MapPinIcon } from '@heroicons/vue/24/solid';


export default {

	emits: [
		'sortOrderChanged',
	],

	props: {
		observations: {
			type: Array as () => Observation[],
			required: true,
		},
		sortOrder: {
			type: Object as () => OrderParameter,
			required: true,
		},
		showPupilColumn: {
			type: Boolean,
			default: true,
		},
		maxThumbnails: {
			type: Number,
			default: 2,
		},
		readOnly: {
			type: Boolean,
			default: false,
		},
		// The classroom this list is viewed from. Rows from any other classroom are
		// labelled with their classroom name; null (organisation level) labels every row.
		classroomId: {
			type: String as PropType<string | null>,
			default: null,
		},
	},

	data() {
		return {
			showObservationEditor: false,
			editableModel: null as Observation | null,
			evaluatingObservation: null as Observation | null,
			observationSaved: false,
			evaluationSuccessMessage: null as string | null,
		};
	},

	components: {
		ObservationEditor,
		ObservationsFilter,
		PupilGoalEvaluation,
		PlusIcon,
		PencilIcon,
		CalendarIcon,
		MapPinIcon,
		SortableHeader,
		Tag,
		PupilSymbol,
		UserAvatar,
	},

	computed: {
		...mapStores(useOrganisationsStore),

		localTime() {
			return (date: Date) => {
				return DateTime.fromJSDate(date).toLocaleString(DateTime.TIME_SIMPLE);
			};
		},

		activityTimeRange() {
			return (lesson: any) => {
				const start = DateTime.fromJSDate(lesson.start);
				const end = DateTime.fromJSDate(lesson.end);
				return start.toLocaleString(DateTime.DATETIME_SHORT).replace(',', ',') + ' - ' + end.toLocaleString(DateTime.TIME_SIMPLE);
			};
		},

	},

	methods: {
		imageFilesFor(observation: Observation) {
			return observation.files.filter((file: any) => file.isImage());
		},

		hiddenImageCount(observation: Observation) {
			return Math.max(this.imageFilesFor(observation).length - this.maxThumbnails, 0);
		},

		openFancyboxAt(observation: Observation, startIndex: number = 0) {
			const images = this.imageFilesFor(observation)
				.map((f: any) => ({ src: f.getVariationOrDefault('lg').url, thumb: f.getVariationOrDefault('sm').url }));

			FancyboxLib.show(images, { startIndex, Toolbar: { display: { left: [], middle: [], right: ['close'] } } });
		},

		registeredObservationDateFormat(date: Date) {
			return DateTime.fromJSDate(date).toLocaleString(DateTime.DATE_FULL);
		},

		closeObservationForm(observation: Observation | null) {
			this.showObservationEditor = false;
			this.editableModel = null;

			// If the saved observation has goals, chain open the evaluation drawer after close animation
			if (observation && this.getGoals(observation).length > 0) {
				this.observationSaved = true;
				this.evaluationSuccessMessage = this.$t('Observation successfully saved');
				this.evaluatingObservation = observation;
			}
		},

		closeEvaluation() {
			this.evaluatingObservation = null;
			this.observationSaved = false;
			this.evaluationSuccessMessage = null;
		},

		handleObservationCreateRequest() {
			this.editableModel = null;
			this.showObservationEditor = true;
		},

		handleActivityEditRequest(observation: Observation) {
			this.editableModel = observation;
			this.showObservationEditor = true;
		},

		handleEvaluateObservation(observation: Observation) {
			this.evaluatingObservation = observation;
		},

		/**
		 * The goals a teacher may evaluate for this observation: those of the observed
		 * lesson/activity that belong to the school's curriculum. Empty without a curriculum.
		 */
		getGoals(observation: Observation): Goal[] {
			return this.organisationsStore.curriculumGoals(observation.relatedGoals);
		},

		/**
		 * Goals of an observation may only be evaluated by users who can act in the classroom
		 * the observation was made in. Deliberately not tied to the observation's author: a
		 * co-teacher must be able to evaluate what a colleague (or a self-observing pupil)
		 * recorded. Observations without a classroom follow the list-wide setting.
		 */
		isReadOnly(observation: Observation): boolean {
			if (this.readOnly) {
				return true;
			}
			if (!observation.classroomId) {
				return false;
			}
			return !this.organisationsStore.canManageClassroom(observation.classroomId);
		},

		showClassroomTag(observation: Observation): boolean {
			return !!observation.classroomName && observation.classroomId !== this.classroomId;
		},
	},
};

</script>
